import { useService } from 'react-reservice';

import { EOptionSymbolSide } from '@aqx-core/common/api/market';
import { TOptionChainData } from '@aqx-core/common/api/option';
import { ESortType, sortNumberAscDescByKey, sortObjAscDescByKey } from '@aqx-core/common/utils/sorting';

import { pickProperties } from '../../../utils';
import { ISymbolDetailData } from '../types';
import { optionService } from './service';
import {
  EUIOptionSide,
  ISelectOptionPriceListListKey,
  IUseSelectedOptionPriceListKeyParams,
  IUseSelectedOptionPriceListStrikeParams
} from './types';

/** Underlying symbol */
export const useUnderlyingSymbolId = () => {
  return useService(optionService, (context) => context.underlyingSymbolId);
};

/** To get the symbol details of the underlying symbol */
export const useUnderlyingSymbolDetailsWithKeys = <K extends keyof ISymbolDetailData>(keys: K[]) => {
  return useService(optionService, (context) => {
    return pickProperties(context.underlyingSymbolDetail || {}, keys);
  });
};

export const useUnderlyingSymbolDetails = () => {
  return useService(optionService, (context) => context.underlyingSymbolDetail);
};

export const useUnderlyingSymbolLoading = () => {
  return useService(optionService, (context) => context.underlyingSymbolLoading);
};

/** Option Related */
export const useOptionListLoading = () => useService(optionService, (context) => context.loading);

export const useOptionSortType = () => useService(optionService, (context) => context.optionSortType || ESortType.ASC);

export const useOptionSide = () => useService(optionService, (context) => context.optionSide || EUIOptionSide.ALL);

export const useOptionExpirationIds = () => {
  return useService(optionService, (context) => context.optionExpirationIds);
};

export const useMappedOptionList = () => {
  return useService(optionService, (context) => context.mappedOptionList);
};

export const useOptionExpirationDetails = (
  expirationId: string
): Pick<TOptionChainData, 'expiration' | 'daysToExpiration'> => {
  return useService(optionService, (context) => {
    const optionData = context.mappedOptionList[expirationId];
    if (!optionData) {
      return {} as Pick<TOptionChainData, 'expiration' | 'daysToExpiration'>;
    }
    return pickProperties(optionData, ['expiration', 'daysToExpiration']);
  });
};

export const useSelectedOptionExpirationIds = () => {
  return useService(optionService, (context) => context.selectedOptionExpirationIds);
};

export const useCheckIsSelectedOptionExpirationIds = (expirationId: string) => {
  return useService(optionService, (context) => context.selectedOptionExpirationIds.includes(expirationId));
};

export const useSelectedOptionPriceListStrike = ({
  expirationId,
  sortedBy
}: IUseSelectedOptionPriceListStrikeParams) => {
  return useService(optionService, (context) => {
    const optionPriceList = context.mappedOptionList[expirationId]?.optionPriceList;
    const optionPriceListSrike =
      optionPriceList && optionPriceList.length > 0 ? optionPriceList.map((option) => option.strikePrice) : [];
    if (!sortedBy) {
      return optionPriceListSrike;
    }
    return sortNumberAscDescByKey(optionPriceListSrike, sortedBy || ESortType.ASC);
  });
};

export const useSelectedOptionPriceListKey = ({
  expirationId,
  side,
  sortedBy
}: IUseSelectedOptionPriceListKeyParams<ISelectOptionPriceListListKey>): ISelectOptionPriceListListKey[] => {
  return useService(optionService, (context) => {
    const selectedOptionPriceList = context.mappedOptionList[expirationId]?.optionPriceList;
    const selectedOptionPriceListKeyArr = selectedOptionPriceList
      ?.filter((option) => option.strikePrice)
      ?.map((option) =>
        side === EOptionSymbolSide.CALL
          ? { symbol: option.call.symbol, expiration: option.call.expiration, strikePrice: option.call.strikePrice }
          : { symbol: option.put.symbol, expiration: option.put.expiration, strikePrice: option.put.strikePrice }
      );
    if (!sortedBy || !sortedBy?.key) {
      return selectedOptionPriceListKeyArr;
    }

    return sortObjAscDescByKey<ISelectOptionPriceListListKey>(
      selectedOptionPriceListKeyArr,
      sortedBy?.key,
      sortedBy?.direction || ESortType.ASC
    );
  });
};

export const useOptionDetail = (symbol: string, expiration: string) => {
  return useService(optionService, (context) => {
    const optionPriceList = context.mappedOptionList[expiration]?.optionPriceList;
    for (const strikePrice in optionPriceList) {
      const options = optionPriceList[strikePrice];
      if (options.call && options.call.symbol === symbol) {
        return options.call;
      }
      if (options.put && options.put.symbol === symbol) {
        return options.put;
      }
    }
    return null;
  });
};

export const useIsInTheMoney = (strikePrice: number, symbolSide: EOptionSymbolSide) => {
  return useService(optionService, (context) => {
    const underlyingSymbolLastPrice = context.underlyingSymbolDetail?.lastPrice;
    if (symbolSide === EOptionSymbolSide.PUT) {
      return strikePrice > (underlyingSymbolLastPrice ?? 0);
    } else if (symbolSide === EOptionSymbolSide.CALL) {
      return strikePrice < (underlyingSymbolLastPrice ?? 0);
    } else {
      return false; // For other symbol types
    }
  });
};

/** To be revisit in future */
// export const useSelectedOptionScrollToPosition = () => {
//   const { expiration, strikePrice } = useSymbolDetailWithKeys(['expiration', 'strikePrice']);
//   const optionSortType = useOptionSortType();
//   // the index of the expiration in the optionExpirationIds
//   const expirationIndex = useOptionExpirationIds().indexOf(expiration ?? '');
//   const sortedOptionPriceListStrike = sortNumberAscDescByKey(
//     [...useSelectedOptionPriceListStrike(expiration ?? '')],
//     optionSortType
//   );
//   const strikePriceIndex = sortedOptionPriceListStrike.indexOf(strikePrice ?? 0);
//   const selectedOptionScrollToPosition = expirationIndex * OPTION_CELL_HEIGHT + strikePriceIndex * OPTION_CELL_HEIGHT;

//   return selectedOptionScrollToPosition;
// };
